import * as d3 from 'd3';
import { BpmnSymbolId, getBpmnSymbol } from '../../data/bpmnSymbols';
import {
  BPMN_ACTIVITY_MARKERS,
  BPMN_ICON_MARKUP,
  isBpmnActivitySymbol,
} from '../../data/bpmnIconMarkup';

const INK = '#1A202C';
const MARKER_ON_DARK = '#F4EEE8';
const DEFAULT_ACTIVITY_FILL = '#c7755b';

interface BpmnRenderOptions {
  width: number;
  height: number;
  fill?: string;
  stroke?: string;
}

function shouldStretchBpmnGlyph(symbolId: BpmnSymbolId): boolean {
  const def = getBpmnSymbol(symbolId);
  if (!def) return false;
  return def.id === 'dataStore' || def.id === 'textAnnotation';
}

function glyphFill(fill?: string, stroke?: string): string {
  const value = (fill || '').toLowerCase();
  if (!value || value === 'transparent' || value === '#fff' || value === '#ffffff' || value === 'white') {
    return stroke || INK;
  }
  return fill || INK;
}

function parseHex(color: string): { r: number; g: number; b: number } | null {
  const value = color.trim();
  const short = /^#([0-9a-f]{3})$/i.exec(value);
  if (short) {
    const [r, g, b] = short[1].split('').map((ch) => parseInt(ch + ch, 16));
    return { r, g, b };
  }
  const full = /^#([0-9a-f]{6})$/i.exec(value);
  if (full) {
    return {
      r: parseInt(full[1].slice(0, 2), 16),
      g: parseInt(full[1].slice(2, 4), 16),
      b: parseInt(full[1].slice(4, 6), 16),
    };
  }
  return null;
}

function relativeLuminance(color: string): number {
  const rgb = parseHex(color);
  if (!rgb) return 0.35;
  const toLinear = (channel: number) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = toLinear(rgb.r);
  const g = toLinear(rgb.g);
  const b = toLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function mixHex(color: string, target: { r: number; g: number; b: number }, amount: number): string {
  const rgb = parseHex(color);
  if (!rgb) return color;
  const mix = (from: number, to: number) => Math.round(from + (to - from) * amount);
  const r = mix(rgb.r, target.r).toString(16).padStart(2, '0');
  const g = mix(rgb.g, target.g).toString(16).padStart(2, '0');
  const b = mix(rgb.b, target.b).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

function activityBodyFill(fill?: string): string {
  const value = (fill || '').toLowerCase();
  if (!value || value === 'transparent' || value === '#fff' || value === '#ffffff' || value === 'white') {
    return DEFAULT_ACTIVITY_FILL;
  }
  return fill || DEFAULT_ACTIVITY_FILL;
}

function activityMarkerFill(bodyFill: string): string {
  return relativeLuminance(bodyFill) > 0.45 ? INK : MARKER_ON_DARK;
}

function appendBpmnActivity(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  symbolId: BpmnSymbolId,
  options: BpmnRenderOptions
) {
  const { width, height, fill, stroke } = options;
  const bodyFill = activityBodyFill(fill);
  const markerFill = activityMarkerFill(bodyFill);
  const strokeWidth = symbolId === 'callActivity' ? 3.5 : 1.5;
  const inset = strokeWidth / 2;
  const radius = Math.min(10, width * 0.08, height * 0.18);
  const bodyStroke = stroke && stroke !== '#ccc' ? stroke : mixHex(bodyFill, { r: 0, g: 0, b: 0 }, 0.28);

  svg.attr('width', width).attr('height', height);

  svg
    .append('rect')
    .attr('class', 'bpmn-activity-body')
    .attr('x', inset)
    .attr('y', inset)
    .attr('width', Math.max(0, width - strokeWidth))
    .attr('height', Math.max(0, height - strokeWidth))
    .attr('rx', radius)
    .attr('ry', radius)
    .attr('fill', bodyFill)
    .attr('stroke', bodyStroke)
    .attr('stroke-width', strokeWidth);

  const marker = BPMN_ACTIVITY_MARKERS[symbolId];
  if (!marker) return svg;

  const bottom = marker.placement === 'bottom-center';
  const markerSize = bottom
    ? Math.max(16, Math.min(22, Math.round(Math.min(width, height) * 0.24)))
    : Math.max(14, Math.min(18, Math.round(Math.min(width, height) * 0.22)));
  const markerX = bottom ? (width - markerSize) / 2 : 6;
  const markerY = bottom ? height - markerSize - 5 : 5;

  svg
    .append('svg')
    .attr('class', bottom ? 'bpmn-activity-marker bpmn-activity-marker-bottom' : 'bpmn-activity-marker')
    .attr('x', markerX)
    .attr('y', markerY)
    .attr('width', markerSize)
    .attr('height', markerSize)
    .attr('viewBox', marker.viewBox)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .append('path')
    .attr('d', marker.d)
    .attr('fill-rule', marker.fillRule)
    .attr('fill', markerFill);

  return svg;
}

/**
 * Renders an IconMagic BPMN glyph (https://iconmagic.io/iconset/bpmn)
 * into a D3 SVG on the swimlane canvas.
 */
export function appendBpmnShape(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  symbolId: BpmnSymbolId,
  options: BpmnRenderOptions
) {
  if (isBpmnActivitySymbol(symbolId)) {
    return appendBpmnActivity(svg, symbolId, options);
  }

  const icon = BPMN_ICON_MARKUP[symbolId];
  if (!icon) return null;

  const { width, height, fill, stroke } = options;
  const stretchToBox = shouldStretchBpmnGlyph(symbolId);

  svg
    .attr('viewBox', icon.viewBox)
    .attr('preserveAspectRatio', stretchToBox ? 'none' : 'xMidYMid meet')
    .attr('width', width)
    .attr('height', height);

  return svg
    .append('path')
    .attr('d', icon.d)
    .attr('fill-rule', icon.fillRule)
    .attr('fill', glyphFill(fill, stroke));
}

export function createBpmnBlockBackground(
  parent: d3.Selection<HTMLDivElement, unknown, null, undefined>,
  symbolId: BpmnSymbolId,
  width: number,
  height: number,
  fill: string,
  stroke: string
) {
  parent.style('background-color', 'transparent');
  parent.style('border', 'none');

  const svg = parent
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .style('position', 'absolute')
    .style('top', '0')
    .style('left', '0')
    .style('overflow', 'visible')
    .style('pointer-events', 'none');

  appendBpmnShape(svg, symbolId, { width, height, fill, stroke });
  return svg;
}

export function updateBpmnActivityGeometry(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  width: number,
  height: number
) {
  svg.attr('width', width).attr('height', height);
  const body = svg.select<SVGRectElement>('rect.bpmn-activity-body');
  if (body.empty()) return;

  const strokeWidth = Number(body.attr('stroke-width') || 1.5);
  const radius = Math.min(10, width * 0.08, height * 0.18);
  body
    .attr('width', Math.max(0, width - strokeWidth))
    .attr('height', Math.max(0, height - strokeWidth))
    .attr('rx', radius)
    .attr('ry', radius);

  const bottomMarker = svg.select<SVGSVGElement>('svg.bpmn-activity-marker-bottom');
  if (!bottomMarker.empty()) {
    const size = Number(bottomMarker.attr('width') || 18);
    bottomMarker.attr('x', (width - size) / 2).attr('y', height - size - 5);
  }
}
